import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import API from "../api/config.js";
import WLogo from "../assets/TravelXOXO 1.png";
import { BsBag } from "react-icons/bs";

const Input = ({
  label,
  id,
  type = "text",
  value,
  onChange,
  required = true,
  placeholder,
}) => (
  <div className="space-y-2">
    <label
      htmlFor={id}
      className="block text-xs font-semibold uppercase text-gray-500 tracking-wider"
    >
      {label} {required && <span className="text-orange-500">*</span>}
    </label>
    <input
      id={id}
      type={type}
      value={value ?? ""}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      className="w-full p-4 border border-gray-200 rounded-xl bg-gray-50 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
    />
  </div>
);

export default function EditRegistration() {
  const { qrId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const verifiedEmail = location.state?.email;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState(false);

  const [form, setForm] = useState({
    name: "",
    phone: "",
    altPhone: "",
    email: "",
    location: "",
    product: "",
    instructions: "",
  });

  const instructionOptions = [
    "Call me if found",
    "WhatsApp me if found",
  ];

  useEffect(() => {
    document.documentElement.classList.remove("dark");
    document.body.className = "bg-gray-50 text-gray-900";
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await API.get(`/registrations/scan/${qrId}`);
        if (!data.registered) {
          setError("This QR is not registered yet.");
          return;
        }

        const r = data.data;
        setForm({
          name: r.owner_name || "",
          phone: r.primary_phone || "",
          altPhone: r.alternate_phone || "",
          email: verifiedEmail || r.email || "",
          location: r.organization_name || "",
          product: r.product_name || "",
          instructions: r.what_to_do_if_found || "",
        });
      } catch (e) {
        setError(e.response?.data?.message || e.message || "Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [qrId, verifiedEmail]);

  const onChange = (e) => {
    const { id, value } = e.target;
    setForm((p) => ({ ...p, [id]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");

    try {
      setSaving(true);
      await API.put(`/registrations/${qrId}`, {
        user_name: form.name,
        email: form.email,
        phone: form.phone,
        alt_phone: form.altPhone || null,
        organization: form.location,
        items: form.product,
        instructions: form.instructions,
      });

      setOk(true);
      setTimeout(() => navigate(`/${qrId}`), 1200);
    } catch (e) {
      setError(e.response?.data?.message || e.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-center">Loading...</div>;
  }

  return (
    <div className="max-w-xl mx-auto p-6 min-h-screen bg-gray-50">
      {/* header */}
      <div className="flex justify-between items-center mb-8">
        <img src={WLogo} alt="Travelxoxo Logo" className="h-10 w-auto object-contain" />

        {/* Shop Now */}
        <div className="flex flex-col items-center gap-1">
          <button
            className="w-12 h-12 rounded-full bg-orange-600 text-white flex items-center justify-center transition-transform duration-200 hover:scale-105 shadow-lg"
            onClick={() => window.open("https://www.travelxoxo.io", "_blank")}
            title="Shop Now"
          >
            <BsBag className="text-white text-xl" />
          </button>
          <span className="text-[10px] font-semibold uppercase tracking-wide text-orange-600">
            Shop Now
          </span>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-xl bg-red-100 text-red-700">
          {error}
        </div>
      )}

      {ok && (
        <div className="mb-4 p-3 rounded-xl bg-green-100 text-green-700">
          Saved successfully
        </div>
      )}

      <form className="space-y-5" onSubmit={onSubmit}>
        <Input label="Name" id="name" value={form.name} onChange={onChange} />
        <Input
          label="Phone"
          id="phone"
          type="tel"
          value={form.phone}
          onChange={onChange}
        />
        <Input
          label="Alternate Phone"
          id="altPhone"
          type="tel"
          required={false}
          value={form.altPhone}
          onChange={onChange}
        />
        <Input
          label="Email"
          id="email"
          type="email"
          value={form.email}
          onChange={onChange}
        />
        <Input
          label="Office / School / College"
          id="location"
          value={form.location}
          onChange={onChange}
        />
        <Input
          label="Product"
          id="product"
          value={form.product}
          onChange={onChange}
        />

        {/* Instruction pills */}
        <div className="space-y-2">
          <label className="block text-xs font-semibold uppercase text-gray-500 tracking-wider">
            What to do if found <span className="text-orange-500">*</span>
          </label>

          <div className="flex flex-wrap gap-2">
            {instructionOptions.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() =>
                  setForm((prev) => ({ ...prev, instructions: option }))
                }
                className={`px-4 py-2 rounded-full text-sm font-semibold border transition
                  ${
                    form.instructions === option
                      ? "bg-orange-600 text-white border-orange-600"
                      : "bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200"
                  }`}
              >
                {option}
              </button>
            ))}
          </div>

          <input type="hidden" value={form.instructions} required />
        </div>

        <button
          type="submit"
          disabled={saving}
          className="w-full py-4 bg-orange-600 text-white font-extrabold rounded-xl hover:bg-orange-700 disabled:opacity-60"
        >
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </form>
    </div>
  );
}
